import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DataGrid,
  Summary,
  TotalItem,
  Column,
  Scrolling,
  FilterRow,
  HeaderFilter,
  FilterPanel,
  ColumnFixing,
} from "devextreme-react/data-grid";
import Moment from "moment";
import { LinearProgress, Alert } from "@mui/material";
import { fetcher } from "../../../../actions";
import useSWR from "swr";
import "./InfoCurrentPeriodsGrid.css"; // Assuming you have a CSS file for styles

const DEFAULT_DATE = "0001-01-01T00:00:00";

const formatDate = (date) => {
  return date === DEFAULT_DATE ? "" : Moment(date).format("yyyy-MM-DD");
};

const InfoCurrentPeriodsGrid = ({ internalId, period, currentMonth, title }) => {

  const fetchUrl = useMemo(() => {
    const baseUrl = `/api/ItemFulfillment/period/${period}/${internalId}`;
    return currentMonth ? `/api/ItemFulfillment/currentMonth/${period}/${internalId}` : baseUrl;
  }, [internalId, period, currentMonth]);


  const {
    data: dataSourceCurrent,
    isLoading,
    error
  } = useSWR(
    fetchUrl,
    fetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
      errorRetryCount: 3
    }
  );

  const gridData = useMemo(() => {
    if (!dataSourceCurrent) return null;
    return dataSourceCurrent.map(obj => ({
      ...obj,
      FormattedDate: formatDate(obj.date)
    }));
  }, [dataSourceCurrent]);

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ margin: 2 }}
      >
        Failed to load transactions data. Please try again.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div
        className="loading-container"
        role="status"
        aria-label="Loading transactions"
      >
        <LinearProgress />
        <span>Loading transactions...</span>
      </div>
    );
  }

  return (
    <div
      id="InfoCurrentPeriodsGrid"
      className="grid-container"
      role="region"
      aria-label={title || "Current Month Transactions"}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        <div className="grid-header">
          {title && <h3 className="grid-title">{title}</h3>}
        </div>
        <div className="grid-wrapper">
          <DataGrid
            dataSource={gridData}
            showBorders={true}
            columnMinWidth={10}
            columnAutoWidth={true}
            height="35vh"
            width="100%"
            columnResizingMode="widget"
            filterMode="withAncestors"
            aria-label="Transactions grid"
          >
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <ColumnFixing enabled={true} />
            <Scrolling mode="infinite" />

            <Column
              dataField="FormattedDate"
              caption="Date"
              dataType="date"
              sortIndex={0}
              sortOrder="desc"
            />
            <Column
              dataField="documentNumber"
              caption="Document Number"
              allowFiltering={true}
            />
            <Column
              dataField="customerName"
              caption="Customer Name"
              allowFiltering={true}
            />
            <Column
              dataField="quantity"
              caption="Quantity"
              dataType="number"
              alignment="right"
            />
            <Column
              dataField="assemblyItem"
              caption="Assembly Item"
              allowFiltering={true}
            />

            <Summary>
              <TotalItem
                column="quantity"
                summaryType="sum"
                displayFormat="Total: {0}"
                alignByColumn={true}
              />
            </Summary>
          </DataGrid>
        </div>
      </div>
    </div>
  );
};

InfoCurrentPeriodsGrid.propTypes = {
  internalId: PropTypes.string.isRequired
};

export default React.memo(InfoCurrentPeriodsGrid);